import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useVenue } from '../context/VenueContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Building2,
  UtensilsCrossed,
  Pizza,
  Wine,
  Loader2,
  LogOut,
  ShieldCheck,
  AlertCircle,
} from 'lucide-react';
import { cn } from '../lib/utils';

const tipoIcon = (tipo) => {
  if (tipo === 'pizzeria') return Pizza;
  if (tipo === 'bar') return Wine;
  if (tipo === 'ristorante') return UtensilsCrossed;
  return Building2;
};

const tipoLabel = (tipo) =>
  ({ ristorante: 'Ristorante', pizzeria: 'Pizzeria', bar: 'Bar' }[tipo] || tipo);

const SelezioneLocalePage = () => {
  const navigate = useNavigate();
  const { user, logout, isSuperAdmin } = useAuth();
  const { availableVenues, loading, selectVenue } = useVenue();

  const handleSelect = (id) => {
    if (selectVenue(id)) navigate('/', { replace: true });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Nessun locale assegnato → schermata bloccante (solo se non super_admin)
  if (availableVenues.length === 0 && !isSuperAdmin()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md" data-testid="no-venue-card">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto">
              <AlertCircle className="w-7 h-7 text-amber-600" />
            </div>
            <h1 className="text-xl font-bold font-heading">Nessun locale assegnato</h1>
            <p className="text-sm text-muted-foreground">
              Il tuo account è autenticato ma non risulta assegnato ad alcun locale.
              Contatta l'amministratore per ricevere le autorizzazioni necessarie.
            </p>
            <Button variant="outline" className="w-full" onClick={handleLogout} data-testid="logout-no-venue-btn">
              <LogOut className="w-4 h-4 mr-2" /> Esci
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 py-10">
      <div className="w-full max-w-md space-y-5" data-testid="venue-picker">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <span>Ciao,</span>
            <span className="font-medium text-foreground">{user?.nome}</span>
            {isSuperAdmin() && (
              <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full text-xs">
                <ShieldCheck className="w-3 h-3" /> Super Admin
              </span>
            )}
          </div>
          <h1 className="text-2xl font-heading font-bold">Seleziona il locale</h1>
          <p className="text-sm text-muted-foreground">
            {isSuperAdmin()
              ? 'Hai accesso a tutti i locali attivi. Scegli uno per operarci.'
              : 'Scegli su quale locale vuoi operare.'}
          </p>
        </div>

        <div className="space-y-3">
          {availableVenues.map((v) => {
            const Icon = tipoIcon(v.tipo);
            return (
              <Card
                key={v.id}
                className="cursor-pointer transition-all hover:border-primary hover:shadow-md"
                onClick={() => handleSelect(v.id)}
                data-testid={`venue-option-${v.slug}`}
              >
                <CardContent className="p-4 flex items-center gap-4">
                  <div className={cn('w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0')}>
                    {v.logo ? (
                      <img src={v.logo} alt={v.nome} className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <Icon className="w-6 h-6 text-primary" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{v.nome}</p>
                    <p className="text-xs text-muted-foreground">
                      {tipoLabel(v.tipo)} · /{v.slug}
                    </p>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">
                    {v.ruolo === 'admin' && 'Admin'}
                    {v.ruolo === 'manager' && 'Manager'}
                    {v.ruolo === 'operatore_cassa' && 'Operatore'}
                    {v.ruolo === 'analista' && 'Analista'}
                  </span>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Button variant="ghost" className="w-full text-muted-foreground" onClick={handleLogout}>
          <LogOut className="w-4 h-4 mr-2" /> Esci
        </Button>
      </div>
    </div>
  );
};

export default SelezioneLocalePage;
